import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { LOCATION_BY_ZIP_CODE_KEY } from '@/data/constants/hdmDataKey';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User-Server';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { traceWithId } from '@/data/utils/loggerUtil';
import { customLocation } from 'integration/generated/custom';
import { LocationFetchResponseEnvelope } from 'integration/generated/custom/data-contracts';
import { GetServerSidePropsContext } from 'next';
import { useCallback } from 'react';
import { unstable_serialize } from 'swr';

const getLocationByZipCodeSWRKey = (storeId: string, zipCode: string) => ({
	storeId,
	zipCode,
});

/**
 * Fetcher for /store/{storeId}/locationFetch/byZipcode
 * pub: true (client) / false (server)
 */
export const locationByZipCodeFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		zipCode: string,
		query: {
			langId: string;
			responseFormat?: 'json' | 'xml';
		}
	): Promise<LocationFetchResponseEnvelope> => {
		const res = await customLocation(pub).locationFetchByZipcode(storeId, {
			zipCode,
			langId: query.langId,
			responseFormat: query.responseFormat ?? 'json',
		});
		return res;
	};

/**
 * Server-side function to fetch locations by ZIP code (conditional).
 * Just call the service if shouldCall === true.
 */
export const getLocationByZipCode = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	zipCode: string,
	shouldCall: boolean
): Promise<LocationFetchResponseEnvelope | null> => {
	const requestId = getRequestId(context);
	traceWithId(requestId, `getLocationByZipCode: start`);
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);

	if (!storeId || !shouldCall) {
		traceWithId(
			requestId,
			`getLocationByZipCode: end (${!storeId ? 'missing storeId' : 'shouldCall=false (skipped)'})`
		);
		return null;
	}

	const props = getLocationByZipCodeSWRKey(storeId, zipCode);
	const key = unstable_serialize([shrink(props), LOCATION_BY_ZIP_CODE_KEY]);
	const cacheScope = getServerCacheScope(context, user.context);

	const value = await cache.get(key);
	if (value) {
		traceWithId(requestId, `getLocationByZipCode: end (from cache)`);
		return value as LocationFetchResponseEnvelope;
	}

	try {
		const response = await locationByZipCodeFetcher(false)(storeId, zipCode, {
			langId,
			responseFormat: 'json',
		});

		if (response) {
			cache.set(key, response, cacheScope);
			traceWithId(requestId, `getLocationByZipCode: end (filled cache)`);
		}
		return response;
	} catch (error) {
		console.error(`Failed to fetch location by zipCode:`, error);
		traceWithId(requestId, `getLocationByZipCode: end (with error)`);
		return null;
	}
};

export const useLocationByZipCode = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);

	const getLocationByZipCode = useCallback(
		async (zipCode: string) => {
			try {
				const a = await locationByZipCodeFetcher(true)(storeId, zipCode, {
					langId,
					responseFormat: 'json',
				});
				return a.response.result;
			} catch (e) {
				throw e;
			}
		},
		[langId, storeId]
	);

	return {
		getLocationByZipCode,
	};
};
